
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Calendar, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getStatusBadgeConfig } from '@/lib/statusBadge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface Campaign {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
  platform_targeted: string[] | null;
  brands?: {
    name: string;
  } | null;
}

interface CalendarCampaignPillProps {
  campaign: Campaign;
  compact?: boolean;
  expanded?: boolean;
}

const CalendarCampaignPill: React.FC<CalendarCampaignPillProps> = ({
  campaign,
  compact = false,
  expanded = false,
}) => {
  const navigate = useNavigate();
  const statusConfig = getStatusBadgeConfig(campaign.status);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/campaigns/${campaign.id}`);
  };

  if (expanded) {
    return (
      <div
        onClick={handleClick}
        className={`${statusConfig.className} border rounded-lg p-4 hover:shadow-md transition-all duration-200 cursor-pointer group`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div 
                className={`w-3 h-3 rounded-full flex-shrink-0 ${statusConfig.dotClassName}`} 
              />
              <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                {campaign.name}
              </h4>
              <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Status:</span>
                <Badge variant="outline" className={`ml-2 ${statusConfig.className}`}>
                  {statusConfig.label}
                </Badge>
              </div>
              
              {campaign.brands?.name && (
                <div>
                  <span className="text-muted-foreground">Brand:</span>
                  <span className="ml-2 font-medium text-foreground">{campaign.brands.name}</span>
                </div>
              )}
              
              {campaign.platform_targeted && campaign.platform_targeted.length > 0 && (
                <div>
                  <span className="text-muted-foreground">Platforms:</span>
                  <span className="ml-2 font-medium text-foreground">
                    {campaign.platform_targeted
                      .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
                      .join(', ')}
                  </span>
                </div>
              )}
              
              {campaign.start_date && campaign.end_date && (
                <div>
                  <span className="text-muted-foreground">Duration:</span>
                  <span className="ml-2 font-medium text-foreground">
                    {new Date(campaign.start_date).toLocaleDateString()} - {new Date(campaign.end_date).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pillContent = (
    <div 
      onClick={handleClick}
      className={`
        ${statusConfig.className} border rounded-full 
        px-3 py-1.5 hover:shadow-sm transition-all duration-200 cursor-pointer
        flex items-center gap-2 group
        ${compact ? 'text-xs' : 'text-sm'}
      `}
    >
      <div 
        className={`w-2 h-2 rounded-full flex-shrink-0 ${statusConfig.dotClassName}`} 
      />
      <span className="font-medium truncate group-hover:text-opacity-80">
        {campaign.name}
      </span>
      {!compact && (
        <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  );

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {pillContent}
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1">
              <div className="font-semibold">{campaign.name}</div>
              <div className="text-xs">Status: {statusConfig.label}</div>
              {campaign.brands?.name && (
                <div className="text-xs">Brand: {campaign.brands.name}</div>
              )}
              {campaign.start_date && campaign.end_date && (
                <div className="text-xs">
                  {new Date(campaign.start_date).toLocaleDateString()} - {new Date(campaign.end_date).toLocaleDateString()}
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return pillContent;
};

export default CalendarCampaignPill;
